import { freshId } from '../forms-model';
import type { Template } from '../layout-model';

/**
 * Widgets génériques de l'éditeur v2 (ADR-146 addendum (14), (16) décision 1, addendum (18)) : nommés par leur
 * nature (« Texte court », « Choix en tuiles », « Code scanné »), jamais par un usage métier. Un usage métier
 * (« Espèce de gibier ») est un préréglage, pas un widget. Chaque widget produit une entrée de champ du contrat
 * `inspection-template-body` 1.10.0 : un type de champ et un `presentation.widget` de la liste fermée du Core.
 * Les widgets d'affichage (image, bouton, puce, synthèse, bandeau d'objet) sont des blocs du registre.
 */

export type Section = Template['sections'][number];
export type Entry = Section['fields'][number];
export type WidgetGroup = 'Saisie' | 'Capture' | 'Affichage';

export interface WidgetSpec {
  id: string;
  label: string;
  /** Nom de la tuile, en un mot (« Compteur ») ; le nom complet passe dans l'infobulle. */
  word: string;
  icon: string;
  group: WidgetGroup;
  /** Ce que fait le widget, en quelques mots (infobulle de la bibliothèque). */
  meta: string;
  /** Types de champ qu'il sait saisir (changement de widget dans l'inspecteur). */
  types: readonly string[];
  /** Entrée de champ créée à l'insertion. */
  create?: (template: Template) => Entry;
  /** Widget d'affichage : bloc du registre, par nom de composant (le premier présent l'emporte). */
  blocks?: readonly string[];
}

const CHOICES = { enum_values: ['choix_1', 'choix_2'], enum_labels: { choix_1: { fr: 'Choix 1' }, choix_2: { fr: 'Choix 2' } } };

function field(template: Template, label: string, type: Entry['field']['type'], extra: Record<string, unknown> = {}, fieldExtra: Record<string, unknown> = {}): Entry {
  const fieldId = freshId(template, 'champ');
  return { ...extra, field: { field_id: fieldId, label: { fr: label }, type, ...fieldExtra } as Entry['field'] } as Entry;
}

function enumeration(template: Template, label: string, widget: 'radio_tiles' | 'segmented' | undefined): Entry {
  return field(template, label, 'enumeration', widget === undefined ? {} : { presentation: { widget } }, { validation: structuredClone(CHOICES) });
}

export const WIDGETS: readonly WidgetSpec[] = [
  { id: 'text', label: 'Texte court', word: 'Texte', icon: 'text', group: 'Saisie', meta: 'Une ligne de texte', types: ['string'], create: (t) => field(t, 'Texte', 'string') },
  { id: 'text_long', label: 'Texte long', word: 'Paragraphe', icon: 'text-long', group: 'Saisie', meta: 'Plusieurs lignes', types: ['rich_text'], create: (t) => field(t, 'Commentaire', 'rich_text') },
  { id: 'number', label: 'Nombre', word: 'Nombre', icon: 'hash', group: 'Saisie', meta: 'Pavé numérique', types: ['number', 'decimal', 'quantity'], create: (t) => field(t, 'Nombre', 'number', { presentation: { widget: 'keypad' } }) },
  {
    id: 'measure',
    label: 'Mesure avec cible',
    word: 'Mesure',
    icon: 'thermometer',
    group: 'Saisie',
    meta: 'Nombre, unité et cible',
    types: ['number', 'decimal', 'quantity'],
    create: (t) => field(t, 'Mesure', 'number', { presentation: { widget: 'keypad', decimals: 1 }, target: { min: 0, max: 4, unit: '°C', on_out_of_target: 'nc_prompt' } }, { unit: '°C' }),
  },
  { id: 'stepper', label: 'Compteur − / +', word: 'Compteur', icon: 'stepper', group: 'Saisie', meta: 'Nombre réglé au pouce', types: ['number', 'decimal', 'quantity'], create: (t) => field(t, 'Quantité', 'number', { presentation: { widget: 'stepper', step: 1 } }) },
  { id: 'slider', label: 'Curseur', word: 'Curseur', icon: 'slider', group: 'Saisie', meta: 'Valeur entre deux bornes', types: ['number', 'decimal'], create: (t) => field(t, 'Niveau', 'number', { presentation: { widget: 'slider' } }, { validation: { min: 0, max: 10 } }) },
  { id: 'stars', label: 'Étoiles', word: 'Étoiles', icon: 'star', group: 'Saisie', meta: 'Note de 1 à 5', types: ['number'], create: (t) => field(t, 'Note', 'number', { presentation: { widget: 'stars' } }, { validation: { min: 1, max: 5 } }) },
  { id: 'toggle', label: 'Oui / Non', word: 'Oui/Non', icon: 'toggle', group: 'Saisie', meta: 'Interrupteur', types: ['boolean'], create: (t) => field(t, 'Oui ou non', 'boolean', { presentation: { widget: 'toggle' } }) },
  { id: 'tiles', label: 'Choix en tuiles', word: 'Tuiles', icon: 'tiles', group: 'Saisie', meta: 'Grandes tuiles, avec image', types: ['enumeration', 'boolean'], create: (t) => enumeration(t, 'Choix', 'radio_tiles') },
  { id: 'segmented', label: 'Choix segmenté', word: 'Segments', icon: 'segmented', group: 'Saisie', meta: 'Deux à quatre choix côte à côte', types: ['enumeration', 'boolean'], create: (t) => enumeration(t, 'Choix', 'segmented') },
  { id: 'dropdown', label: 'Liste déroulante', word: 'Liste', icon: 'list', group: 'Saisie', meta: 'Beaucoup de choix', types: ['enumeration'], create: (t) => enumeration(t, 'Choix', undefined) },
  { id: 'date', label: 'Date', word: 'Date', icon: 'calendar', group: 'Saisie', meta: 'Jour, sans heure', types: ['date'], create: (t) => field(t, 'Date', 'date', { presentation: { widget: 'date' } }) },
  { id: 'datetime', label: 'Date et heure', word: 'Heure', icon: 'clock', group: 'Saisie', meta: 'Jour et heure', types: ['datetime'], create: (t) => field(t, 'Date et heure', 'datetime', { presentation: { widget: 'datetime' } }) },
  { id: 'scan_code', label: 'Code scanné', word: 'Code', icon: 'scan', group: 'Capture', meta: 'Caméra ou douchette', types: ['string'], create: (t) => field(t, 'Code', 'string', { presentation: { widget: 'scan_code', scan: { sources: ['camera', 'wedge'], manual_entry: true } } }) },
  { id: 'scan_doc', label: 'Document scanné', word: 'Document', icon: 'file', group: 'Capture', meta: 'Fiche lue par la caméra', types: ['string'], create: (t) => field(t, 'Document', 'string', { presentation: { widget: 'scan_doc' } }) },
  { id: 'lot', label: 'Lot', word: 'Lot', icon: 'tag', group: 'Capture', meta: 'Numéro de lot', types: ['string'], create: (t) => field(t, 'Lot', 'string', { presentation: { widget: 'lot' } }) },
  { id: 'selector', label: 'Objet à choisir', word: 'Objet', icon: 'link', group: 'Capture', meta: 'Un objet de la base', types: ['reference'], create: (t) => field(t, 'Objet', 'reference', { presentation: { widget: 'selector', selector: { label_field: 'display_name', search: true } } }, { reference_type: 'Location' }) },
  {
    // Champ « Personne » (ADR-152) : une référence à une personne choisie par rôle ; le rôle se règle dans l'inspecteur.
    id: 'person',
    label: 'Personne',
    word: 'Personne',
    icon: 'user',
    group: 'Capture',
    meta: 'Une personne, choisie par son rôle',
    types: ['reference'],
    create: (t) => field(t, 'Personne', 'reference', { presentation: { widget: 'person', variant: 'field' } }, { reference_type: 'Party' }),
  },
  { id: 'photo', label: 'Photo', word: 'Photo', icon: 'camera', group: 'Capture', meta: 'Preuve en image', types: ['string'], create: (t) => field(t, 'Photo', 'string', { presentation: { widget: 'photo' }, evidence: { min_count: 1, kind: 'photo' } }) },
  { id: 'signature', label: 'Signature', word: 'Signature', icon: 'signature', group: 'Capture', meta: 'Signature au doigt', types: ['string'], create: (t) => field(t, 'Signature', 'string', { presentation: { widget: 'signature' }, evidence: { min_count: 1, kind: 'signature' } }) },
  { id: 'image', label: 'Image', word: 'Image', icon: 'image', group: 'Affichage', meta: 'Une image de la médiathèque', types: [], blocks: ['FormImage', 'Image'] },
  { id: 'action', label: 'Bouton d’action', word: 'Bouton', icon: 'pointer', group: 'Affichage', meta: 'Ajouter une ligne, ouvrir…', types: [], blocks: ['FormButton', 'ActionButton'] },
  { id: 'chip', label: 'Puce de compteur', word: 'Total', icon: 'chip', group: 'Affichage', meta: 'Un total en pastille', types: [], blocks: ['AggregateChip', 'CounterChip', 'FormCounter'] },
  { id: 'summary', label: 'Synthèse', word: 'Synthèse', icon: 'sigma', group: 'Affichage', meta: 'Totaux de la fiche', types: [], blocks: ['FormSummary'] },
  { id: 'banner', label: 'Bandeau d’objet', word: 'Bandeau', icon: 'id-card', group: 'Affichage', meta: 'L’objet de la tâche', types: [], blocks: ['ObjectBanner'] },
];

export const widgetById = (id: string): WidgetSpec | undefined => WIDGETS.find((widget) => widget.id === id);

/** Widget de l'éditeur qui décrit une entrée de champ (pour l'inspecteur et les étiquettes). */
export function widgetOf(entry: Entry): WidgetSpec {
  const presentation = entry.presentation as { widget?: string } | undefined;
  const widget = presentation?.widget;
  const type = entry.field.type;
  const by = (id: string): WidgetSpec => widgetById(id)!;
  switch (widget) {
    case 'radio_tiles':
      return by('tiles');
    case 'segmented':
      return by('segmented');
    case 'stepper':
      return by('stepper');
    case 'slider':
      return by('slider');
    case 'stars':
      return by('stars');
    case 'toggle':
      return by('toggle');
    case 'scan_code':
      return by('scan_code');
    case 'scan_doc':
      return by('scan_doc');
    case 'lot':
      return by('lot');
    case 'photo':
      return by('photo');
    case 'signature':
      return by('signature');
    case 'selector':
      return by('selector');
    case 'person':
      return by('person');
    case 'datetime':
      return by('datetime');
    case 'date':
      return by('date');
    case 'keypad':
      return entry.target !== undefined ? by('measure') : by('number');
    default:
      break;
  }
  if (type === 'enumeration') return by('dropdown');
  if (type === 'boolean') return by('toggle');
  if (type === 'reference') return by('selector');
  if (type === 'date') return by('date');
  if (type === 'datetime') return by('datetime');
  if (type === 'rich_text') return by('text_long');
  if (['number', 'decimal', 'quantity', 'money'].includes(type)) return entry.target !== undefined ? by('measure') : by('number');
  return by('text');
}

/** `presentation.widget` du contrat pour un widget de l'éditeur (absent : rendu par défaut du type). */
export const CONTRACT_WIDGET: Readonly<Record<string, string | undefined>> = {
  text: undefined,
  text_long: undefined,
  number: 'keypad',
  measure: 'keypad',
  stepper: 'stepper',
  slider: 'slider',
  stars: 'stars',
  toggle: 'toggle',
  tiles: 'radio_tiles',
  segmented: 'segmented',
  dropdown: undefined,
  date: 'date',
  datetime: 'datetime',
  scan_code: 'scan_code',
  scan_doc: 'scan_doc',
  lot: 'lot',
  selector: 'selector',
  person: 'person',
  photo: 'photo',
  signature: 'signature',
};

/**
 * Widgets de saisie qui conviennent au type d'un champ (choix « Widget » de l'inspecteur). « Personne » n'en est
 * pas : il porte sa source (rôle, famille), il se dépose depuis la bibliothèque.
 */
export const widgetsFor = (type: string): WidgetSpec[] => WIDGETS.filter((widget) => widget.create !== undefined && widget.id !== 'person' && widget.types.includes(type));

/** Variante d'un champ « Personne » déposé : la puce dans un bandeau, le champ ailleurs (ADR-152 §4). */
export function personInRegion(entry: Entry, strip: boolean): Entry {
  if ((entry.presentation as { widget?: string } | undefined)?.widget !== 'person') return entry;
  return { ...entry, presentation: { ...(entry.presentation as Record<string, unknown>), variant: strip ? 'chip' : 'field' } } as Entry;
}

/** Change le widget d'une entrée sans toucher à sa donnée : seul `presentation.widget` change. */
export function changeWidget(entry: Entry, widgetId: string): Entry {
  const contract = CONTRACT_WIDGET[widgetId];
  const presentation = { ...((entry.presentation ?? {}) as Record<string, unknown>) };
  if (contract === undefined) delete presentation['widget'];
  else presentation['widget'] = contract;
  const next = { ...entry } as Entry & Record<string, unknown>;
  if (Object.keys(presentation).length === 0) delete next['presentation'];
  else next['presentation'] = presentation;
  return next;
}

/** Widgets dont la valeur est une liste de choix (options, visuels, disposition). */
export const isChoice = (entry: Entry): boolean => entry.field.type === 'enumeration' || (entry.field.type === 'boolean' && ['tiles', 'segmented'].includes(widgetOf(entry).id));
export const isNumeric = (entry: Entry): boolean => ['number', 'decimal', 'quantity', 'money'].includes(entry.field.type);
export const isScan = (entry: Entry): boolean => ['scan_code', 'lot'].includes(widgetOf(entry).id);
/** Widgets qui admettent la variante « bouton » (contrat 1.10.0). */
export const BUTTON_VARIANT = new Set(['selector', 'date', 'datetime', 'scan_code', 'scan_doc', 'lot', 'photo']);
